/**
 * Running minimum and maximum over a stream of points.
 *
 * Shared by every file parser: each of them walks a file's coordinates once and wants the
 * extent, and four copies of six comparisons is four places for a `<` to be a `>`.
 */

import type { Bounds, Vec3 } from './types.js';

export class BoundsAccumulator {
  private minX = Infinity;
  private minY = Infinity;
  private minZ = Infinity;
  private maxX = -Infinity;
  private maxY = -Infinity;
  private maxZ = -Infinity;

  private seen = false;

  /**
   * Include a point.
   *
   * @returns false if any coordinate is not finite, in which case nothing is recorded.
   * A NaN admitted here would poison every comparison after it and produce bounds that
   * are neither right nor obviously wrong.
   */
  add(x: number, y: number, z: number): boolean {
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) return false;

    if (x < this.minX) this.minX = x;
    if (y < this.minY) this.minY = y;
    if (z < this.minZ) this.minZ = z;
    if (x > this.maxX) this.maxX = x;
    if (y > this.maxY) this.maxY = y;
    if (z > this.maxZ) this.maxZ = z;

    this.seen = true;
    return true;
  }

  /** Whether any point has been included. Empty bounds are infinite, not zero. */
  get isEmpty(): boolean {
    return !this.seen;
  }

  bounds(): Bounds {
    const min: Vec3 = [this.minX, this.minY, this.minZ];
    const max: Vec3 = [this.maxX, this.maxY, this.maxZ];
    return { min, max };
  }
}

/**
 * How far two measurements of one model may differ and still be the same measurement.
 *
 * A model measured twice by different routes — our own parser and the renderer's loader —
 * differs only in the last bits of a float. Anything that changes what a file *means*, on
 * the other hand, changes it by orders of magnitude: reading a glTF's units as metres
 * rather than millimetres moves every number by a factor of a thousand. One per cent sits
 * far above the first and far below the second.
 */
const AGREEMENT = 0.01;

/**
 * Whether two measurements of the same model agree.
 *
 * Asked of a stored record against what was actually built from its file. They can come
 * apart when the rule for reading a format changes, and a record measured under the old
 * rule then describes a model that no longer exists — wrong in the library, and wrong for
 * anything that places or frames it.
 */
export function boundsAgree(stored: Bounds, measured: Bounds): boolean {
  // Relative to the larger of the two, so a tolerance means the same thing for a model
  // measured in microns as for one measured in metres. The floor keeps a model with no
  // extent at all — a single point, or a flat plane — from demanding exactness no float
  // can promise.
  const extent = Math.max(...spanOf(stored), ...spanOf(measured), 1);
  const allowed = extent * AGREEMENT;

  return ([0, 1, 2] as const).every(
    (axis) =>
      Math.abs(stored.min[axis] - measured.min[axis]) <= allowed &&
      Math.abs(stored.max[axis] - measured.max[axis]) <= allowed,
  );
}

function spanOf(bounds: Bounds): [number, number, number] {
  return [
    bounds.max[0] - bounds.min[0],
    bounds.max[1] - bounds.min[1],
    bounds.max[2] - bounds.min[2],
  ];
}
